import { useCallback, useState, type HTMLAttributes, type ReactNode } from 'react';
import { formatCount, pluralise } from './format.js';
import { Icon } from './Icon.js';

// A headed section the reader can fold away (03-components §13): the header row's button is the trigger, with a
// chevron that turns, `aria-expanded` on it and the panel named by `aria-controls`. The heading wraps the button (the
// WAI-ARIA accordion pattern) so it stays a heading — a heading inside a button is flattened to plain text. The count
// stays in the header while the section is folded (04-data §1.6: folding must never hide that entries exist).

/** Why sessionStorage: §13 asks expansion state to persist within a session; a new session starts with everything open. */
const STORAGE_PREFIX = 'calis:section-open:';

function readStoredOpen(key: string, fallback: boolean): boolean {
  // Why the guards: storage may be absent (rendering under node in the tests) or refuse access (private windows,
  // blocked site data); the section then simply starts as its default.
  try {
    if (typeof window === 'undefined') return fallback;
    const stored = window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
}

function writeStoredOpen(key: string, open: boolean): void {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, open ? '1' : '0');
  } catch {
    // Not remembered: the section still opens and closes for this page.
  }
}

/** Whether a section is open, remembered for the rest of the browser session under `key`. */
export function useSessionOpen(key: string, defaultOpen = true): [boolean, (open: boolean) => void] {
  const [open, setOpenState] = useState(() => readStoredOpen(key, defaultOpen));
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeStoredOpen(key, next);
  }, [key]);
  return [open, setOpen];
}

export interface CollapsibleSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title' | 'children' | 'id'> {
  /** The section's id (an anchor a link may name); the heading is `<id>-heading`, the visible title `<id>-title` and the panel `<id>-panel`. */
  id: string;
  title: ReactNode;
  /** Shown beside the title whether open or folded; leave out where the section has nothing to count. */
  count?: number;
  /** What the count counts, for a screen reader ("3 items" by default). */
  countLabel?: (count: number) => string;
  headingLevel?: 2 | 3 | 4;
  /** Content at the end of the header row, outside the trigger (a link to elsewhere, a date). */
  aside?: ReactNode;
  /**
   * Opens a folded section while it holds what the address points at (a `#…` deep link): the token names that
   * target. Folding it again releases it, until a different target arrives.
   */
  revealFor?: string | null;
  /** Where the open state is remembered; the id by default. */
  storageKey?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function CollapsibleSection({
  id,
  title,
  count,
  countLabel,
  headingLevel = 3,
  aside,
  revealFor = null,
  storageKey,
  defaultOpen = true,
  className,
  children,
  ...rest
}: CollapsibleSectionProps) {
  const [open, setOpen] = useSessionOpen(storageKey ?? id, defaultOpen);
  const [releasedFor, setReleasedFor] = useState<string | null>(null);
  // Why only after a toggle: every section entering with a fade on page load would be motion nobody asked for.
  const [toggled, setToggled] = useState(false);
  const forced = revealFor !== null && revealFor !== releasedFor;
  const shown = open || forced;
  const toggle = (): void => {
    setToggled(true);
    if (shown) {
      setOpen(false);
      if (forced) setReleasedFor(revealFor);
    } else {
      setOpen(true);
    }
  };
  const Heading = `h${headingLevel}` as const;
  return (
    <section
      {...rest}
      id={id}
      className={['collapsible', shown ? 'is-open' : '', className ?? ''].filter(Boolean).join(' ')}
      aria-labelledby={`${id}-title`}
    >
      <div className="collapsible__head">
        <Heading id={`${id}-heading`} className="collapsible__heading">
          <button type="button" className="collapsible__trigger" aria-expanded={shown} aria-controls={`${id}-panel`} onClick={toggle}>
            <Icon name="chevron-down" size={16} className="collapsible__chevron" />
            <span id={`${id}-title`} className="collapsible__title">{title}</span>
            {count !== undefined ? (
              <>
                <span className="collapsible__count num" aria-hidden="true">{formatCount(count)}</span>
                <span className="visually-hidden">{`, ${countLabel ? countLabel(count) : pluralise(count, { one: '# item', other: '# items' })}`}</span>
              </>
            ) : null}
          </button>
        </Heading>
        {aside ? <div className="collapsible__aside">{aside}</div> : null}
      </div>
      <div id={`${id}-panel`} className={['collapsible__panel', toggled ? 'is-entering' : ''].filter(Boolean).join(' ')} hidden={!shown}>
        {children}
      </div>
    </section>
  );
}
